import {Grid, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import React, {ReactNode, useEffect, useMemo, useRef, useState} from "react";
import {
    ElementApiClient,
    StereotypeFromPropertyUpdateDto,
    StereotypeFromPropertyUpdateReportDto
} from "../../../../../common/apis/element/ElementApiClient";
import {ElementDto} from "../../../../../common/apis/element/ElementDto";
import {ValidationError} from "../../../../../common/ValidationError";
import {NegativeButtons, PositiveButtons} from "../../../../../components/button/ButtonLayout";
import {CancelButton} from "../../../../../components/button/CancelButton";
import {SaveButton} from "../../../../../components/button/SaveButton";
import Dialog from "../../../../../components/dialogs/Dialog";
import DialogActions from "../../../../../components/dialogs/DialogActions";
import DialogContent from "../../../../../components/dialogs/DialogContent";
import DialogTitle from "../../../../../components/dialogs/DialogTitle";
import MessageDialog from "../../../../../components/dialogs/MessageDialog";
import SingleSelectField from "../../../../../components/fields/select/SingleSelectField";
import {t} from "../../../../../store/localization/TranslMessasge";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import Snackbar from "../../snackbar/Snackbar";
import {ElementErrorCode} from "../ElementErrorCode";
import {ElementTranslationKey} from "../ElementTranslationKey";

interface StereotypeFromPropertyUdpateDialogProps {
    open: boolean,
    onClose: () => void,
    elements: ElementDto[]
    onUpdated: () => void,
}

export default function StereotypeFromPropertyUpdateDialog(props: Readonly<StereotypeFromPropertyUdpateDialogProps>) {
    const {open, onClose, elements, onUpdated} = props;

    const [selectedPropertyName, setSelectedPropertyName] = useState<string | null>(null);
    const [reportDialogOpen, setReportDialogOpen] = useState<boolean>(false);
    const [reportContent, setReportContent] = useState<ReactNode | null>(null);

    const elementApiClient = useRef(new ElementApiClient());

    useEffect(() => {
        if (open) {
            return () => {
                setSelectedPropertyName(null);
            }
        }
    }, [open, elements]);

    const propertyNames: string[] = useMemo(() => {
        if (open && elements.length > 0) {
            const propNames = new Set(elements.flatMap(element => element.properties ?? [])
                    .map(property => property.definition.name));

            return Array.from(propNames)
                    .sort( (a, b) => a.localeCompare(b));
        } else {
            return [];
        }
    }, [open, elements]);

    function updateStereotypeFromProperty() {
        if (!selectedPropertyName) {
            return;
        }

        const stereotypeFromPropertyUpdate: StereotypeFromPropertyUpdateDto = {
            identifiers: elements.map(value => value.identifier),
            stereotypePropertyName: selectedPropertyName,
        };
        elementApiClient.current.updateStereotypeFromProperty(stereotypeFromPropertyUpdate)
                .then((report) => {
                    openReportDialog(report);
                    onUpdated();
                })
                .catch((error) => {
                    if (error instanceof ValidationError) {
                        if (error.error.code === ElementErrorCode.ELEMENT_TYPE_DOES_NOT_CONFORM_TO_STEREOTYPE) {
                            Snackbar.warning(t(ElementTranslationKey.ELEMENT_TYPE_DOES_NOT_CONFORM_TO_BASE_TYPE_OF_STEREOTYPE,
                                    {
                                        elementType: error.error.arguments?.elementType,
                                        stereotypeBaseType: error.error.arguments?.stereotypeElementType,
                                        stereotypeName: error.error.arguments?.stereotypeName
                                    }));
                        } else if (error.error.code === ElementErrorCode.STEREOTYPE_ALREADY_ASSIGNED) {
                            Snackbar.warning(t(ElementTranslationKey.ELEMENT_HAS_ALREADY_BEEN_ASSIGNED_WITH_STEREOTYPE,
                                    {elementName: error.error.arguments?.elementName}));
                        } else if (error.error.code === ElementErrorCode.UNKNOWN_STEREOTYPE) {
                            Snackbar.warning(t(ElementTranslationKey.NO_STEREOTYPE_WITH_NAME_EXISTS,
                                    {stereotypeName: error.error.arguments?.stereotypeName}));
                        } else {
                            Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED));
                        }
                    } else {
                        Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED));
                    }
                });
    }

    function openReportDialog(report: StereotypeFromPropertyUpdateReportDto) {
        const totalCount = report.updatedCount + report.skippedCount;
        const updatedElementsReportMessage = t(ElementTranslationKey.COUNT_OUT_OF_TOTAL_ELEMENTS_HAS_BEEN_ASSIGNED_STEREOTYPE,
                {totalCount: totalCount, assignedCount: report.updatedCount});
        if (report.skippedCount > 0) {
            const remainingElementsSkippedMessage = t(ElementTranslationKey.REMAINIG_ELEMENTS_WERE_SKIPPED);
            setReportContent((
                    <Stack spacing={2} direction={"column"}>
                        <Typography variant={"body1"}>{updatedElementsReportMessage}</Typography>
                        <Typography variant={"body1"}>{remainingElementsSkippedMessage}</Typography>
                    </Stack>
            ));
        } else {
            setReportContent(updatedElementsReportMessage);
        }

        setReportDialogOpen(true);
    }

    function closeReportDialog() {
        setReportDialogOpen(false);
        onClose();
    }

    return (
        <>
            <Dialog open={open}
                    aria-labelledby="dialog-title"
                    onClose={onClose}
                    maxWidth={'md'}>
                <DialogTitle id="dialog-title"
                             title={t(ElementTranslationKey.SET_ELEMENT_STEREOTYPE_FROM_PROPERTY)}
                             onDialogClosed={onClose}/>
                <DialogContent>
                    <Grid container spacing={2} mt={0}>
                        <Grid item xs={12}>
                            <SingleSelectField
                                    label={t(ElementTranslationKey.PROPERTY_NAME)}
                                    value={selectedPropertyName}
                                    onChange={setSelectedPropertyName}
                                    options={propertyNames}
                                    getOptionLabel={(prop) => prop}/>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <NegativeButtons>
                        <CancelButton onClick={onClose}/>
                    </NegativeButtons>
                    <PositiveButtons>
                        <SaveButton onClick={updateStereotypeFromProperty} disabled={!selectedPropertyName}/>
                    </PositiveButtons>
                </DialogActions>
            </Dialog>
            
            <MessageDialog
                    open={reportDialogOpen}
                    title={t(ElementTranslationKey.REPORT)}
                    content={reportContent}
                    onClose={closeReportDialog}/>
        </>
    );
}